import React, { useState } from 'react';
import { useVehicleBookings } from '../../hooks/useVehicleBookings';
import { VehicleBooking, BookingStatus } from '../../types/vehicle';
import { 
  CalendarIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  ClockIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  TruckIcon,
  PencilIcon,
  PlusIcon,
  ListBulletIcon,
  CalendarDaysIcon,
  TrashIcon,
  Bars3BottomLeftIcon
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';
import { Timestamp } from 'firebase/firestore';
import CommonPageHeader from '../common/CommonPageHeader';
import LoadingScreen from '../common/LoadingScreen';
import ConfirmDeleteModal from '../common/ConfirmDeleteModal';
import BookingEditor from './BookingEditor';
import BookingsCalendar from './BookingsCalendar';
import BookingsGantt from './BookingsGantt';

const BookingsList: React.FC = () => {
  const { bookings, loading, error, loadBookings, updateStatus, createBooking, updateBooking, deleteBooking } = useVehicleBookings();
  const [selectedStatus, setSelectedStatus] = useState<BookingStatus | 'all'>('all');
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editingBooking, setEditingBooking] = useState<VehicleBooking | undefined>();
  const [viewMode, setViewMode] = useState<'list' | 'calendar' | 'gantt'>('list');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState<{ id: string; info: string } | null>(null);

  const formatDate = (date: Date | Timestamp | undefined) => {
    if (!date) return '';
    const dateObj = date instanceof Timestamp ? date.toDate() : date;
    return format(dateObj, 'yyyy. MMM dd.', { locale: hu });
  };

  const handleStatusChange = async (bookingId: string, newStatus: BookingStatus) => {
    try {
      setUpdatingId(bookingId);
      await updateStatus(bookingId, newStatus);
      // A realtime listener automatikusan frissíti a listát
    } catch (error) {
      console.error('Error updating booking status:', error);
      alert('Hiba történt a státusz frissítése során');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleSaveBooking = async (bookingData: Partial<VehicleBooking>) => {
    try {
      if (editingBooking) {
        // Szerkesztés
        await updateBooking(editingBooking.id, bookingData);
      } else {
        // Új foglalás
        await createBooking(bookingData as any);
      }
    } catch (error) {
      console.error('Error saving booking:', error);
      throw error;
    }
  };

  const handleEditBooking = (booking: VehicleBooking) => {
    setEditingBooking(booking);
    setShowEditor(true);
  };

  const handleNewBooking = () => {
    setEditingBooking(undefined);
    setShowEditor(true);
  };

  const handleCloseEditor = () => {
    setShowEditor(false);
    setEditingBooking(undefined);
  };

  const handleDeleteBooking = (bookingId: string, bookingInfo: string) => {
    setBookingToDelete({ id: bookingId, info: bookingInfo });
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!bookingToDelete) return;

    try {
      setUpdatingId(bookingToDelete.id);
      await deleteBooking(bookingToDelete.id);
      setShowDeleteModal(false);
      setBookingToDelete(null);
      // A realtime listener automatikusan frissíti a listát
    } catch (error) {
      console.error('Error deleting booking:', error);
      alert('Hiba történt a foglalás törlése során');
    } finally {
      setUpdatingId(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setBookingToDelete(null);
  };

  const getStatusColor = (status: BookingStatus) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'confirmed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'completed':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusLabel = (status: BookingStatus) => {
    switch (status) {
      case 'pending':
        return 'Függőben';
      case 'confirmed':
        return 'Jóváhagyva';
      case 'cancelled':
        return 'Törölve';
      case 'completed':
        return 'Befejezve';
      default:
        return status;
    }
  };

  const filteredBookings = selectedStatus === 'all' 
    ? bookings 
    : bookings.filter(b => b.status === selectedStatus);

  if (loading) {
    return <LoadingScreen />;
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <p className="text-red-800 dark:text-red-200">Hiba: {error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <CommonPageHeader
          title="Foglalások"
          subtitle="Autó foglalások kezelése és jóváhagyása"
          icon={CalendarIcon}
        />
        <div className="flex items-center gap-3">
          {/* View Mode Toggle */}
          <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'list'
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <ListBulletIcon className="w-4 h-4" />
              Lista
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'calendar'
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <CalendarDaysIcon className="w-4 h-4" />
              Naptár
            </button>
            <button
              onClick={() => setViewMode('gantt')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'gantt'
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <Bars3BottomLeftIcon className="w-4 h-4" />
              Gantt
            </button>
          </div>
          
          <button
            onClick={handleNewBooking}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <PlusIcon className="h-5 w-5" />
            Új foglalás
          </button>
        </div>
      </div>

      {/* Info Bar */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
        <div className="flex items-center gap-2 text-sm text-blue-800 dark:text-blue-300">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>
            <strong>Tipp:</strong> 
            {viewMode === 'list' 
              ? ' Kattints a "Szerkesztés" gombra a módosításhoz, vagy a "Törlés" gombra az eltávolításhoz.'
              : ' Bal klikk a foglaláson = szerkesztés, jobb klikk = törlés.'
            }
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedStatus('all')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              selectedStatus === 'all'
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Összes ({bookings.length})
          </button>
          <button
            onClick={() => setSelectedStatus('pending')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              selectedStatus === 'pending'
                ? 'bg-yellow-600 text-white'
                : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
            }`}
          >
            Függőben ({bookings.filter(b => b.status === 'pending').length})
          </button>
          <button
            onClick={() => setSelectedStatus('confirmed')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              selectedStatus === 'confirmed'
                ? 'bg-green-600 text-white'
                : 'bg-green-100 text-green-700 hover:bg-green-200'
            }`}
          >
            Jóváhagyva ({bookings.filter(b => b.status === 'confirmed').length})
          </button>
          <button
            onClick={() => setSelectedStatus('completed')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              selectedStatus === 'completed'
                ? 'bg-blue-600 text-white'
                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
            }`}
          >
            Befejezve ({bookings.filter(b => b.status === 'completed').length})
          </button>
          <button
            onClick={() => setSelectedStatus('cancelled')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              selectedStatus === 'cancelled'
                ? 'bg-red-600 text-white'
                : 'bg-red-100 text-red-700 hover:bg-red-200'
            }`}
          >
            Törölve ({bookings.filter(b => b.status === 'cancelled').length})
          </button>
        </div>
      </div>

      {/* Views */}
      {viewMode === 'calendar' ? (
        <BookingsCalendar 
          bookings={filteredBookings}
          onBookingClick={handleEditBooking}
          onBookingDelete={handleDeleteBooking}
        />
      ) : viewMode === 'gantt' ? (
        <BookingsGantt 
          bookings={filteredBookings}
          onBookingClick={handleEditBooking}
          onBookingDelete={handleDeleteBooking}
        />
      ) : (
        /* Bookings List */
        <div className="space-y-4">
          {filteredBookings.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <CalendarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Nincs megjeleníthető foglalás</p>
            </div>
          ) : (
            filteredBookings.map((booking) => (
            <div key={booking.id} className="bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-md transition-shadow">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <TruckIcon className="h-6 w-6 text-indigo-600" />
                      <h3 className="text-lg font-semibold text-gray-900">
                        {booking.vehicleMake} {booking.vehicleModel}
                      </h3>
                      <span className="text-sm text-gray-500">
                        ({booking.vehicleLicensePlate})
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <CalendarIcon className="h-4 w-4" />
                        <span>
                          {formatDate(booking.startDate)}
                          {' - '}
                          {formatDate(booking.endDate)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(booking.status)}`}>
                    {getStatusLabel(booking.status)}
                  </span>
                </div>

                {/* Customer Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Ügyfél adatok</h4>
                    <div className="space-y-1 text-sm">
                      <p className="font-medium text-gray-900">{booking.userName}</p>
                      <div className="flex items-center gap-2 text-gray-600">
                        <EnvelopeIcon className="h-4 w-4" />
                        <span>{booking.userEmail}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <PhoneIcon className="h-4 w-4" />
                        <span>{booking.userPhone}</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Helyszínek</h4>
                    <div className="space-y-1 text-sm text-gray-600">
                      {booking.pickupLocation && (
                        <div className="flex items-center gap-2">
                          <MapPinIcon className="h-4 w-4 text-green-600" />
                          <span>Átvétel: {booking.pickupLocation}</span>
                        </div>
                      )}
                      {booking.dropoffLocation && (
                        <div className="flex items-center gap-2">
                          <MapPinIcon className="h-4 w-4 text-red-600" />
                          <span>Leadás: {booking.dropoffLocation}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Price Info */}
                <div className="flex items-center justify-between mb-4 p-3 bg-indigo-50 rounded-lg">
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">{booking.pricePerDay?.toLocaleString()} {booking.currency || 'HUF'}/nap</span>
                    {booking.depositAmount && (
                      <span className="ml-4">Kaució: {booking.depositAmount.toLocaleString()} {booking.currency || 'HUF'}</span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEditBooking(booking)}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    <PencilIcon className="h-5 w-5" />
                    Szerkesztés
                  </button>
                  <button
                    onClick={() => handleDeleteBooking(
                      booking.id, 
                      `${booking.vehicleMake} ${booking.vehicleModel} - ${booking.userName} (${formatDate(booking.startDate)} - ${formatDate(booking.endDate)})`
                    )}
                    disabled={updatingId === booking.id}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    <TrashIcon className="h-5 w-5" />
                    Törlés
                  </button>
                  {booking.status === 'pending' && (
                    <>
                      <button
                        onClick={() => handleStatusChange(booking.id, 'confirmed')}
                        disabled={updatingId === booking.id}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                      >
                        <CheckCircleIcon className="h-5 w-5" />
                        Jóváhagyás
                      </button>
                      <button
                        onClick={() => handleStatusChange(booking.id, 'cancelled')}
                        disabled={updatingId === booking.id}
                        className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                      >
                        <XCircleIcon className="h-5 w-5" />
                        Elutasítás
                      </button>
                    </>
                  )}
                  {booking.status === 'confirmed' && (
                    <button
                      onClick={() => handleStatusChange(booking.id, 'completed')}
                      disabled={updatingId === booking.id}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      <CheckCircleIcon className="h-5 w-5" />
                      Befejezés
                    </button>
                  )}
                  {booking.status === 'completed' && (
                    <span className="flex items-center gap-2 text-sm text-gray-600">
                      <ClockIcon className="h-5 w-5" />
                      Lezárva
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
        </div>
      )}

      {/* Booking Editor Modal */}
      {showEditor && (
        <BookingEditor
          booking={editingBooking}
          onSave={handleSaveBooking}
          onClose={handleCloseEditor}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Foglalás törlése"
        deleteMessage="Biztosan törölni szeretnéd ezt a foglalást?"
        deleteWarning={bookingToDelete?.info || ''}
        danger={false}
      />
    </div>
  );
};

export default BookingsList;
